/**
 * @fileoverview Internationalization context provider.
 * @package PlayItNow
 * @module apps/front/contexts/I18nContext
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { t, getCurrentLanguage, setCurrentLanguage, type Language } from '@/lib/i18n'

/**
 * @summary Shape of the i18n context value.
 */
interface I18nContextType {
  /** Current active language */
  lang: Language
  /** Translation function */
  t: (key: string, fallback?: string) => string
  /** Switch to a different language */
  changeLanguage: (newLang: Language) => void
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

/**
 * @summary Props for the I18nProvider component.
 */
interface I18nProviderProps {
  /** Child components */
  children: ReactNode
}

/**
 * @summary I18n context provider.
 */
export function I18nProvider({ children }: I18nProviderProps) {
  const [lang, setLang] = useState<Language>(getCurrentLanguage())

  useEffect(() => {
    setCurrentLanguage(lang)
  }, [lang])

  const changeLanguage = (newLang: Language) => {
    setLang(newLang)
    setCurrentLanguage(newLang)
  }

  const translate = (key: string, fallback?: string) => t(lang, key, fallback)

  return (
    <I18nContext.Provider value={{ lang, t: translate, changeLanguage }}>
      {children}
    </I18nContext.Provider>
  )
}

/**
 * @summary Hook to use i18n context.
 */
export function useTranslation() {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider')
  }
  return context
}
